<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SalesPartyPhoneField from './SalesPartyPhoneField.vue';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';
import {
  normalizeSalesPartyIdentityInput,
  salesPartyIdentityFieldError,
  salesPartyIdentityGenders,
  salesPartyIdentityInputMode,
  salesPartyIdentityInputValue,
  salesPartyIdentityLookupLocked,
  salesPartyIdentityMaxLength,
  salesPartyIdentityRequired,
  salesPartyIdentityRows,
  salesPartyIdentityShowsLookupIndicator,
} from '../composables/salesPartyIdentityFields';

const props = defineProps({
  errors: { type: Object, default: () => ({}) },
  hasFormContext: { type: Boolean, default: false },
  identity: { type: Object, default: () => ({}) },
  internalProfile: { type: Boolean, default: false },
  lookupError: { type: String, default: '' },
  lookupLoading: { type: Boolean, default: false },
  roleLabel: { type: String, default: '' },
});

const emit = defineEmits(['update']);
const { t } = useI18n();
const hasRg = computed(() => (props.identity.rg || '').trim().length >= 4);
const fieldRows = computed(() =>
  salesPartyIdentityRows({
    hasFormContext: props.hasFormContext,
    roleLabel: props.roleLabel,
    t,
  })
);
const genders = computed(() => salesPartyIdentityGenders(t));
const fieldError = field =>
  salesPartyIdentityFieldError({
    errors: props.errors,
    field,
    lookupError: props.lookupError,
  });
const fieldValue = field =>
  salesPartyIdentityInputValue(props.identity, field) || '';
const hasFieldValue = field => Boolean(String(fieldValue(field)).trim());
const fieldDisabled = field =>
  salesPartyIdentityLookupLocked({
    field,
    lookupLoading: props.lookupLoading,
    internalProfile: props.internalProfile,
  }) ||
  (['rg_issuer', 'rg_issued_on'].includes(field) && !hasRg.value);
const referenceControlClasses = ({ filled, disabled, error }) => {
  const classes = [
    'reset-base h-8 w-full border-0 border-b bg-transparent px-0 text-base outline-none placeholder:text-[#A7B7BE] focus:ring-0',
  ];
  if (error) {
    classes.push('border-[#D64545] text-[#D64545]');
  } else if (filled && !disabled) {
    classes.push('border-[#009F94] font-bold text-[#009F94]');
  } else {
    classes.push('border-[#A7B7BE] text-[#7F8C93]');
  }
  if (disabled) classes.push('cursor-not-allowed text-[#7F8C93]');
  return classes;
};
const update = (field, value) =>
  emit('update', field, normalizeSalesPartyIdentityInput(field, value));
</script>

<template>
  <div class="grid gap-4">
    <div
      v-for="(row, rowIndex) in fieldRows"
      :key="rowIndex"
      :data-testid="`sales-party-personal-row-${rowIndex}`"
      class="grid gap-4"
      :class="row.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'"
    >
      <template v-for="[field, label, placeholder, type] in row" :key="field">
        <SalesPartyPhoneField
          v-if="field === 'phone'"
          :country-code="identity.country_code"
          :disabled="internalProfile"
          :phone="identity.phone"
          :error="errors.phone"
          @update="update"
        />
        <SalesPropertyReferenceField
          v-else-if="field === 'gender'"
          :filled="Boolean(identity.gender)"
          :disabled="lookupLoading"
          indicator="select"
          :label="t('SALES_PARTIES.FORM.GENDER')"
          :error="errors.gender"
          required
          test-id="sales-party-identity-gender"
        >
          <select
            :value="identity.gender || ''"
            :disabled="lookupLoading"
            :class="
              referenceControlClasses({
                disabled: lookupLoading,
                error: errors.gender,
                filled: Boolean(identity.gender),
              })
            "
            class="appearance-none pr-5"
            @change="update('gender', $event.target.value)"
          >
            <option
              v-for="option in genders"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </SalesPropertyReferenceField>
        <SalesPropertyReferenceField
          v-else
          :filled="hasFieldValue(field)"
          :disabled="fieldDisabled(field)"
          :label="label"
          :error="fieldError(field)"
          :required="salesPartyIdentityRequired({ field, hasRg })"
          :show-success="
            field !== 'email' &&
            !salesPartyIdentityShowsLookupIndicator({ field, lookupLoading })
          "
          :test-id="`sales-party-identity-${field}`"
        >
          <div class="relative">
            <input
              :value="fieldValue(field)"
              :type="type || 'text'"
              :inputmode="salesPartyIdentityInputMode(field)"
              :maxlength="salesPartyIdentityMaxLength(field)"
              :placeholder="placeholder"
              :disabled="fieldDisabled(field)"
              :class="
                referenceControlClasses({
                  disabled: fieldDisabled(field),
                  error: fieldError(field),
                  filled: hasFieldValue(field),
                })
              "
              @input="update(field, $event.target.value)"
            />
            <span
              v-if="
                salesPartyIdentityShowsLookupIndicator({ field, lookupLoading })
              "
              aria-hidden="true"
              class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-lais-primary"
              data-testid="sales-party-lookup-loading"
            >
              <span class="i-lucide-loader-circle size-4 animate-spin" />
            </span>
          </div>
        </SalesPropertyReferenceField>
      </template>
    </div>
  </div>
</template>
